// Temporary, per-tab memory of the last offering search per type, so re-entering a
// "Find …" page via the bare sidebar links restores the student's last criteria.
// sessionStorage scopes it to the tab; the TTL bounds it within a long-lived tab.
// (Browser Back already restores criteria via the URL — this covers fresh re-entry.)
import type { OfferingType } from "@/services/offeringSearchService";

const KEY_PREFIX = "bmc:offering-search:";
const TTL_MS = 30 * 60 * 1000; // 30 minutes

interface Saved {
  query: string;
  savedAt: number;
}

const keyFor = (type: OfferingType) => `${KEY_PREFIX}${type}`;

/** Persist the URL query string of the current search for `type`. */
export function saveSearch(type: OfferingType, query: string): void {
  try {
    const payload: Saved = { query, savedAt: Date.now() };
    sessionStorage.setItem(keyFor(type), JSON.stringify(payload));
  } catch {
    /* storage unavailable (e.g. private mode) — non-fatal */
  }
}

/** Return the last saved query string for `type`, or null if absent/expired. */
export function loadSearch(type: OfferingType): string | null {
  try {
    const raw = sessionStorage.getItem(keyFor(type));
    if (!raw) return null;
    const { query, savedAt } = JSON.parse(raw) as Saved;
    if (!query || typeof savedAt !== "number" || Date.now() - savedAt > TTL_MS) {
      sessionStorage.removeItem(keyFor(type));
      return null;
    }
    return query;
  } catch {
    return null;
  }
}

/** Forget the saved search for `type` (e.g. when the user clears all filters). */
export function clearSearch(type: OfferingType): void {
  try {
    sessionStorage.removeItem(keyFor(type));
  } catch {
    /* non-fatal */
  }
}

export type SearchMemoryAction =
  | { kind: "restore"; query: string }
  | { kind: "save"; query: string }
  | { kind: "clear" };

/**
 * Decide what to do with the per-type search memory on a render. Pure except for
 * the sessionStorage read inside loadSearch, so it can be unit-tested.
 *
 * - On a fresh entry to a type (prevType changed) with no criteria in the URL,
 *   restore that type's saved search if one exists. Restoring returns early, so
 *   switching types never clears the type you just left.
 * - Otherwise persist the current criteria, or forget them when the page is bare
 *   within the same type (i.e. the user cleared all filters).
 */
export function decideSearchMemory(params: {
  prevType: OfferingType | null;
  offeringType: OfferingType;
  hasSearchCriteria: boolean;
  currentQuery: string;
}): SearchMemoryAction {
  const { prevType, offeringType, hasSearchCriteria, currentQuery } = params;
  if (prevType !== offeringType && !hasSearchCriteria) {
    const saved = loadSearch(offeringType);
    if (saved) return { kind: "restore", query: saved };
  }
  return hasSearchCriteria ? { kind: "save", query: currentQuery } : { kind: "clear" };
}
